import React, { useEffect, useRef } from 'react';

/**
 * HeroBackground — sophisticated animated background for the Landing hero.
 *
 * Visual layers (back-to-front):
 *   1. Animated mesh-gradient (slowly morphing emerald/teal/cyan)
 *   2. Flowing data-stream particles connected by faint lines (think: cloud spend
 *      flowing through a graph)
 *   3. Subtle grid overlay for "data" feel
 *
 * Built to feel cinematic but professional — NOT cheesy. No emojis, no falling
 * dollar signs. The metaphor is "data nodes connecting" which IS what we do.
 */
const HeroBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    // Performance: only animate particles on devices that won't choke
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);

    // Init particles — a denser neural-network feel (no boxes, no grid)
    const PARTICLE_COUNT = window.innerWidth < 768 ? 45 : 90;
    const COLORS = [
      'rgba(16,185,129,', // emerald
      'rgba(20,184,166,', // teal
      'rgba(6,182,212,',  // cyan
      'rgba(56,189,248,', // sky
      'rgba(34,211,238,', // light cyan (extra variation)
    ];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.25,  // slower, smoother drift
        vy: (Math.random() - 0.5) * 0.25,
        radius: Math.random() * 1.4 + 0.6,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        opacity: Math.random() * 0.45 + 0.35,
        pulse: Math.random() * Math.PI * 2, // for soft pulsing
      });
    }

    let frame = 0;
    const draw = () => {
      frame++;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      // Update + draw particles
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;

        // Soft wrap around edges
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        // Connecting lines — wider radius for richer neural mesh
        for (let j = i + 1; j < particles.length; j++) {
          const q = particles[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const distSq = dx * dx + dy * dy;
          const maxDist = 170; // up from 130 — denser network
          if (distSq < maxDist * maxDist) {
            const opacity = (1 - Math.sqrt(distSq) / maxDist) * 0.22;
            ctx.strokeStyle = `rgba(56,189,248,${opacity})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }

        // Soft pulse so each node breathes
        const pulse = Math.sin((frame + p.pulse * 60) * 0.03) * 0.2 + 0.8;
        const r = p.radius * pulse;

        // Particle dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `${p.color}${p.opacity})`;
        ctx.fill();

        // Glow halo
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 5);
        grad.addColorStop(0, `${p.color}${p.opacity * 0.5})`);
        grad.addColorStop(1, `${p.color}0)`);
        ctx.fillStyle = grad;
        ctx.fillRect(p.x - r * 5, p.y - r * 5, r * 10, r * 10);
      }

      animationId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      aria-hidden="true"
      data-testid="hero-bg"
    >
      {/* Mesh gradient backdrop — slowly morphing */}
      <div className="absolute inset-0 opacity-60">
        <div className="absolute -top-20 -left-20 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-emerald-500/30 via-teal-500/15 to-transparent blur-3xl animate-mesh-1" />
        <div className="absolute top-40 -right-20 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-cyan-500/25 via-sky-500/10 to-transparent blur-3xl animate-mesh-2" />
        <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-teal-500/20 to-transparent blur-3xl animate-mesh-3" />
      </div>

      {/* Animated neural-network canvas (no grid, no boxes) */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full opacity-80 mix-blend-screen"
      />

      {/* Soft fade at bottom so content below blends in */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-slate-950 bg-gradient-to-t from-slate-950 to-transparent" />
    </div>
  );
};

export default HeroBackground;
